import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DialogIncludesTypes } from '@app/core/models/diallog';
import { NotificationService } from '@app/modules/notification-modules/notification.service';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import { FileToBase64 } from '@app/utils/converter';
import { VenueGalleryEditDialogData } from './venue-gallery-edit-dialog.model';

const MAX_FILE_MB = 1;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

@UntilDestroy()
@Component({
  selector: 'app-venue-gallery-edit-dialog',
  templateUrl: './venue-gallery-edit-dialog.component.html',
  styleUrls: ['./venue-gallery-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VenueGalleryEditDialogComponent {
  bookingCustomerId: string;
  keys: string[] = [];
  existingUrls: string[] = [];
  newItems: { preview: string; key: string }[] = [];
  uploadLoading = false;
  applyLoading = false;

  constructor(
    public ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private bookingService: BookingService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {
    const data = (this.config.data || {}) as VenueGalleryEditDialogData;
    this.bookingCustomerId = data.bookingCustomerId ?? '';
    this.keys = Array.isArray(data.currentKeys) ? [...data.currentKeys] : [];
    this.existingUrls = Array.isArray(data.currentPhotoUrls) ? [...data.currentPhotoUrls] : [];
  }

  get allKeys(): string[] {
    return [...this.keys, ...this.newItems.map(i => i.key)];
  }

  onFileChange(files: FileList, inputEl: HTMLInputElement): void {
    if (!files?.length || !this.bookingCustomerId) {
      return;
    }
    const file = files[0];
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: 'Допустимые форматы: PNG, JPG, JPEG.'
      });
      if (inputEl) {
        inputEl.value = '';
      }
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: `Размер файла не должен превышать ${MAX_FILE_MB} Мб.`
      });
      return;
    }
    this.uploadLoading = true;
    this.cdr.markForCheck();
    FileToBase64(file).then((dataUrl: unknown) => {
      const preview = (dataUrl as string) || '';
      this.bookingService.uploadBookingCustomerPhoto(file).pipe(untilDestroyed(this)).subscribe(
        key => {
          this.newItems.push({ preview, key: key || '' });
          this.uploadLoading = false;
          if (inputEl) {
            inputEl.value = '';
          }
          this.cdr.markForCheck();
        },
        err => {
          this.uploadLoading = false;
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupErrorTemplate,
            header: 'Ошибка загрузки',
            content: (err && (err.message || err.error)) || 'Не удалось загрузить фото.'
          });
          if (inputEl) {
            inputEl.value = '';
          }
          this.cdr.markForCheck();
        }
      );
    });
  }

  removeExisting(i: number): void {
    this.keys.splice(i, 1);
    this.existingUrls.splice(i, 1);
    this.cdr.markForCheck();
  }

  removeNew(i: number): void {
    this.newItems.splice(i, 1);
    this.cdr.markForCheck();
  }

  onApply(): void {
    const id = this.bookingCustomerId;
    if (!id) {
      return;
    }
    const picKeys = this.allKeys.filter(Boolean);
    this.applyLoading = true;
    this.cdr.markForCheck();
    this.bookingService.updateVenuePhotos(id, { picKeys }).pipe(untilDestroyed(this)).subscribe(
      () => {
        this.applyLoading = false;
        this.ref.close({ applied: true, picKeys });
        this.cdr.markForCheck();
      },
      err => {
        this.applyLoading = false;
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка',
          content: (err && (err.message || err.error)) || 'Не удалось обновить фотоальбом.'
        });
        this.cdr.markForCheck();
      }
    );
  }

  onCancel(): void {
    this.ref.close();
  }
}
